import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Clock, Truck, Package, Circle } from "@phosphor-icons/react";

// Simple in-memory cache + subscribers so ReceptionChip and filters share state
let _cache = null;
let _inflight = null;
const _subs = new Set();

function _notify() {
  for (const cb of _subs) cb(_cache);
}

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = api
    .get("/catalogs/reception-statuses")
    .then(({ data }) => {
      _cache = data || [];
      _notify();
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export function invalidateReceptionStatuses() {
  _cache = null;
  _fetch();
}

export function useReceptionStatuses() {
  const [list, setList] = useState(_cache || []);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const cb = (data) => {
      setList(data || []);
      setLoading(false);
    };
    _subs.add(cb);
    if (_cache === null) {
      setLoading(true);
      _fetch();
    } else {
      cb(_cache);
    }
    return () => _subs.delete(cb);
  }, []);

  const reload = useCallback(() => {
    invalidateReceptionStatuses();
  }, []);

  return { list, loading, reload };
}

/** Pick an icon by key or heuristics on the label */
export function iconForStatus(s) {
  if (!s) return Circle;
  if (s.is_progress) return Truck;
  if (s.is_final) return Package;
  if (s.is_default) return Clock;
  const l = (s.label || "").toLowerCase();
  if (l.includes("pend")) return Clock;
  if (l.includes("proc") || l.includes("cargando") || l.includes("descarg")) return Truck;
  if (l.includes("descarg") || l.includes("final") || l.includes("recib")) return Package;
  return Circle;
}

/** Convert hex color to rgba() for backgrounds */
export function withAlpha(hex, alpha = 0.14) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return `rgba(167, 139, 250, ${alpha})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
